import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../../utils/api'
import AdminLayout from '../components/AdminLayout';
import { useSortableData, SortDirection } from '../components/SortableTable';

interface Publication {
  id: number;
  brandProfileId: number;
  name: string;
  avatarUrl?: string;
  role: string;
  addedOn: string;
  stories: number;
  pitches: number;
}

interface UserBasic {
  id: string;
  fullName: string;
  email: string;
}

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  margin-bottom: 16px;
  color: #6b7280;
  a { color: #00a6c2; text-decoration: none; &:hover { text-decoration: underline; } }
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 24px 0;
`;

const TabNav = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 24px;
`;

const TabLink = styled(Link)<{ $active?: boolean }>`
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ $active }) => ($active ? '#00a6c2' : '#6b7280')};
  text-decoration: none;
  border-bottom: 2px solid ${({ $active }) => ($active ? '#00a6c2' : 'transparent')};
  margin-bottom: -1px;
  &:hover { color: #00a6c2; background: #f9fafb; }
`;

const Section = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 24px;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 20px 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th<{ $sortable?: boolean; $sorted?: boolean }>`
  text-align: left;
  padding: 12px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ $sorted }) => ($sorted ? '#00a6c2' : '#374151')};
  border-bottom: 2px solid #e5e7eb;
  background: #f9fafb;
  cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
  user-select: none;
  white-space: nowrap;
  
  &:hover {
    ${({ $sortable }) => $sortable && `
      background: #f0f0f0;
      color: #00a6c2;
    `}
  }
`;

const SortIndicator = styled.span<{ $direction: SortDirection }>`
  margin-left: 4px;
  
  &::after {
    content: '${({ $direction }) => {
      if ($direction === 'asc') return '↓';
      if ($direction === 'desc') return '↑';
      return '';
    }}';
  }
`;

const Td = styled.td`
  padding: 12px;
  font-size: 14px;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: middle;
`;

const Avatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  object-fit: cover;
  margin-right: 12px;
  vertical-align: middle;
`;

const PlaceholderAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  background: #e5e7eb;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  vertical-align: middle;
  color: #9ca3af;
  font-size: 12px;
`;

const PubLink = styled(Link)`
  color: #00a6c2;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const ActionButton = styled.button`
  padding: 6px 10px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 4px;
  filter: drop-shadow(0 0 0.5px rgba(0, 0, 0, 0.3));
  &:hover { background: #e5e7eb; }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px;
  color: #6b7280;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 24px;
  color: #6b7280;
`;

function AdminUserPublicationsPage() {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<UserBasic | null>(null);
  const [publications, setPublications] = useState<Publication[]>([]);
  const [removedStaffings, setRemovedStaffings] = useState<Publication[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Sorting for publications table
  const { sortedData: sortedPublications, requestSort, getSortDirection } = useSortableData(publications, { key: 'addedOn', direction: 'asc' });
  
  // Sorting for removed staffings table  
  const { sortedData: sortedRemovedStaffings, requestSort: requestSortRemoved, getSortDirection: getSortDirectionRemoved } = useSortableData(removedStaffings, { key: 'addedOn', direction: 'asc' });

  const fetchData = useCallback(async () => {
    try {
      const [userRes, pubsRes] = await Promise.all([
        api.get(`/admin2/users/${id}.json`),
        api.get(`/admin2/users/${id}/publications.json`)
      ]);
      setUser({
        id: userRes.data.id,
        fullName: `${userRes.data.firstName || ''} ${userRes.data.lastName || ''}`.trim() || userRes.data.email,
        email: userRes.data.email
      });
      setPublications(pubsRes.data.publications || []);
      setRemovedStaffings(pubsRes.data.removedStaffings || []);
    } catch (err) {
      console.error('Failed to load data', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <AdminLayout title="Users" pageTitle="User Publications">
        <LoadingState>Loading...</LoadingState>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Users" pageTitle={`${user?.fullName || 'User'} - Publications`}>
      <Breadcrumb>
        <Link to="/admin2/users">Users</Link>
        <span>›</span>
        <Link to={`/admin2/users/${id}/edit`}>{user?.fullName} ({user?.email})</Link>
        <span>›</span>
        <span>Publications</span>
      </Breadcrumb>

      <PageTitle>{user?.fullName}</PageTitle>

      <TabNav>
        <TabLink to={`/admin2/users/${id}/edit`}>User info</TabLink>
        <TabLink to={`/admin2/users/${id}/publications`} $active>Publications</TabLink>
        <TabLink to={`/admin2/users/${id}/stories`}>Stories</TabLink>
        <TabLink to={`/admin2/users/${id}/audit_events`}>Audit Events</TabLink>
        <TabLink to={`/admin2/users/${id}/content_origin_access`}>Content Origin Access</TabLink>
      </TabNav>

      <Section>
        <SectionTitle>Publications</SectionTitle>
        {publications.length === 0 ? (
          <EmptyState>No publications found for this user.</EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th $sortable $sorted={getSortDirection('name') !== null} onClick={() => requestSort('name')}>
                  Name <SortIndicator $direction={getSortDirection('name')} />
                </Th>
                <Th $sortable $sorted={getSortDirection('role') !== null} onClick={() => requestSort('role')}>
                  Role <SortIndicator $direction={getSortDirection('role')} />
                </Th>
                <Th $sortable $sorted={getSortDirection('addedOn') !== null} onClick={() => requestSort('addedOn')}>
                  Added on <SortIndicator $direction={getSortDirection('addedOn')} />
                </Th>
                <Th $sortable $sorted={getSortDirection('stories') !== null} onClick={() => requestSort('stories')}>
                  Stories <SortIndicator $direction={getSortDirection('stories')} />
                </Th>
                <Th $sortable $sorted={getSortDirection('pitches') !== null} onClick={() => requestSort('pitches')}>
                  Pitches <SortIndicator $direction={getSortDirection('pitches')} />
                </Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {sortedPublications.map((pub) => (
                <tr key={pub.id}>
                  <Td>
                    {pub.avatarUrl ? (
                      <Avatar src={pub.avatarUrl} alt={pub.name} />
                    ) : (
                      <PlaceholderAvatar>📄</PlaceholderAvatar>
                    )}
                    <PubLink to={`/admin2/publications/${pub.brandProfileId}/edit`}>{pub.name}</PubLink>
                  </Td>
                  <Td>{pub.role}</Td>
                  <Td>{pub.addedOn}</Td>
                  <Td>{pub.stories}</Td>
                  <Td>{pub.pitches}</Td>
                  <Td>
                    <ActionButton title="Edit">✏️</ActionButton>
                    <ActionButton title="Stories">📚</ActionButton>
                    <ActionButton title="Remove">👤</ActionButton>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>

      <Section>
        <SectionTitle>Publications history</SectionTitle>
        {removedStaffings.length === 0 ? (
          <EmptyState>No removed staffings</EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th $sortable $sorted={getSortDirectionRemoved('name') !== null} onClick={() => requestSortRemoved('name')}>
                  Name <SortIndicator $direction={getSortDirectionRemoved('name')} />
                </Th>
                <Th $sortable $sorted={getSortDirectionRemoved('role') !== null} onClick={() => requestSortRemoved('role')}>
                  Role <SortIndicator $direction={getSortDirectionRemoved('role')} />
                </Th>
                <Th $sortable $sorted={getSortDirectionRemoved('addedOn') !== null} onClick={() => requestSortRemoved('addedOn')}>
                  Removed on <SortIndicator $direction={getSortDirectionRemoved('addedOn')} />
                </Th>
              </tr>
            </thead>
            <tbody>
              {sortedRemovedStaffings.map((pub) => (
                <tr key={pub.id}>
                  <Td>{pub.name}</Td>
                  <Td>{pub.role}</Td>
                  <Td>{pub.addedOn}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>
    </AdminLayout>
  );
}

export default AdminUserPublicationsPage;
